import React, {
    useState, useEffect, useRef
} from 'react'
import MapboxGL from 'mapbox-gl';
import { FeatureCollection, Feature } from 'geojson'
import FloorplanDark from '../map-styles/floorplan-dark';
import FloorplanLight from '../map-styles/floorplan-light'
import { FloorplanModelType } from '../interface-type';
import { createModelLayer } from './use-func/model3d';


const FloorplanModel: React.FunctionComponent<FloorplanModelType> = (props) => {
    const ref = useRef<any>(null)
    const [statemap, setstatemap] = useState<MapboxGL.Map | undefined>(undefined)
    const [oldFloorplan, setoldFloorplan] = useState<{
        id: string;
        origin: [number, number];
        model: string, device: FeatureCollection
    } | undefined>(undefined)
    const { floorplan, theme, selectedStyle, selectDevice, onClickDevice, onMapUpdate, ...mapOptions } = props

    const handleClickDevice = (evt: any) => {
        onClickDevice(evt)
    }

    const onMouseenter = (e: any) => {
        e.target.getCanvas().style.cursor = 'pointer';
    }
    const onMouseleave = (e: any) => {
        e.target.getCanvas().style.cursor = '';

    }

    useEffect(() => {
        const map = new MapboxGL.Map({
            container: ref.current,
            style: theme === 'dark' ? FloorplanDark : FloorplanLight,
            antialias: true,
            ...mapOptions
        })
        map.on('load', () => {
            if (floorplan) {
                const { device, ...building } = floorplan
                const model3dLayer = createModelLayer(building)
                const addSelectFieldFeatures = device.features.map((feature: Feature) => {
                    feature.properties = { ...feature.properties, select: false }
                    return feature
                })
                map.addLayer(model3dLayer)
                map.addSource(`device-${floorplan.id}`, {
                    type: 'geojson',
                    data: { ...device, features: addSelectFieldFeatures }
                })
                map.addLayer({
                    id: `device-${floorplan.id}`,
                    source: `device-${floorplan.id}`,
                    type: 'fill-extrusion',
                    filter: ['==', ['get', 'select'], false],
                    'paint': {
                        'fill-extrusion-color': selectedStyle.color,
                        'fill-extrusion-height': ['get', 'height'],
                        'fill-extrusion-base': ['get', 'base'],
                        'fill-extrusion-opacity': 0
                    }
                })
                map.addLayer({
                    id: `device-selected-${floorplan.id}`,
                    source: `device-${floorplan.id}`,
                    type: 'fill-extrusion',
                    filter: ['==', ['get', 'select'], true],
                    'paint': {
                        'fill-extrusion-color': selectedStyle.color,
                        'fill-extrusion-height': ['get', 'height'],
                        'fill-extrusion-base': ['get', 'base'],
                        'fill-extrusion-opacity': selectedStyle.opacity
                    }
                })
                map.on('mouseleave', `device-${floorplan.id}`, onMouseleave)
                map.on('mouseenter', `device-${floorplan.id}`, onMouseenter)
                map.on('mouseleave', `device-selected-${floorplan.id}`, onMouseleave)
                map.on('mouseenter', `device-selected-${floorplan.id}`, onMouseenter)
                setoldFloorplan(floorplan)
            }
            setstatemap(map)
        })
        return () => {
            if (floorplan) {
                map.off('mouseleave', `device-${floorplan.id}`, onMouseleave)
                map.off('mouseenter', `device-${floorplan.id}`, onMouseenter)
                map.off('mouseleave', `device-selected-${floorplan.id}`, onMouseleave)
                map.off('mouseenter', `device-selected-${floorplan.id}`, onMouseenter)
            }
            map.remove()
            setstatemap(undefined)
        };
    }, [])

    useEffect(() => {
        if (statemap) {
            statemap.setPaintProperty(
                'background',
                "background-color",
                theme === 'dark' ?
                    FloorplanDark.layers[0].paint['background-color']
                    : FloorplanLight.layers[0].paint['background-color'])
        }
    }, [statemap, theme])

    useEffect(() => {
        if (statemap && floorplan) {
            if (oldFloorplan) {
                statemap.removeLayer(oldFloorplan.id)
                statemap.removeLayer(`device-${oldFloorplan.id}`)
                statemap.removeLayer(`device-selected-${oldFloorplan.id}`)
                statemap.removeSource(`device-${oldFloorplan.id}`)
            }
            const { device, ...building } = floorplan
            const model3dLayer = createModelLayer(building)
            const addSelectFieldFeatures = device.features.map((feature: Feature) => {
                feature.properties = { ...feature.properties, select: false }
                return feature
            })
            statemap.addSource(`device-${floorplan.id}`, {
                type: 'geojson',
                data: { ...device, features: addSelectFieldFeatures }
            })
            statemap.addLayer(model3dLayer)
            statemap.addLayer({
                id: `device-${floorplan.id}`,
                source: `device-${floorplan.id}`,
                type: 'fill-extrusion',
                filter: ['==', ['get', 'select'], false],
                'paint': {
                    'fill-extrusion-color': selectedStyle.color,
                    'fill-extrusion-height': ['get', 'height'],
                    'fill-extrusion-base': ['get', 'base'],
                    'fill-extrusion-opacity': 0
                }
            })
            statemap.addLayer({
                id: `device-selected-${floorplan.id}`,
                source: `device-${floorplan.id}`,
                type: 'fill-extrusion',
                filter: ['==', ['get', 'select'], true],
                'paint': {
                    'fill-extrusion-color': selectedStyle.color,
                    'fill-extrusion-height': ['get', 'height'],
                    'fill-extrusion-base': ['get', 'base'],
                    'fill-extrusion-opacity': selectedStyle.opacity
                }
            })
            statemap.on('mouseleave', `device-${floorplan.id}`, onMouseleave)
            statemap.on('mouseenter', `device-${floorplan.id}`, onMouseenter)
            statemap.on('mouseleave', `device-selected-${floorplan.id}`, onMouseleave)
            statemap.on('mouseenter', `device-selected-${floorplan.id}`, onMouseenter)
            statemap.on('click', `device-${floorplan.id}`, handleClickDevice)
            statemap.on('click', `device-selected-${floorplan.id}`, handleClickDevice)
            setoldFloorplan(floorplan)
        }
        return () => {
            if (statemap && floorplan) {
                statemap.off('click', `device-${floorplan.id}`, handleClickDevice)
                statemap.off('click', `device-selected-${floorplan.id}`, handleClickDevice)
                statemap.off('mouseleave', `device-${floorplan.id}`, onMouseleave)
                statemap.off('mouseenter', `device-${floorplan.id}`, onMouseenter)
                statemap.off('mouseleave', `device-selected-${floorplan.id}`, onMouseleave)
                statemap.off('mouseenter', `device-selected-${floorplan.id}`, onMouseenter)
            }
        };
    }, [statemap, floorplan, oldFloorplan])

    useEffect(() => {
        if (statemap && floorplan && statemap.getSource(`device-${floorplan.id}`)) {
            const deviceSource: MapboxGL.GeoJSONSource | any = statemap.getSource(`device-${floorplan.id}`)
            deviceSource.setData({
                ...deviceSource._options.data, "features": floorplan.device.features.map((feature: Feature | any) => {
                    if (feature.properties.code && feature.properties.code === selectDevice) {
                        feature.properties.select = true
                    }
                    else {
                        feature.properties.select = false
                    }
                    return feature
                })
            });
        }

    }, [statemap, selectDevice, floorplan])



    useEffect(() => {
        if (statemap) {
            onMapUpdate(statemap)
        }
    }, [statemap])

    return (
        <div ref={ref} style={{ height: "100%", width: "100%" }} />
    )
}

FloorplanModel.defaultProps = {
    theme: 'dark',
    selectedStyle: { color: '#ffffff', opacity: 0.4 },
    onMapUpdate: () => { },
    onClickDevice: () => { }
}

export default FloorplanModel
